// src/hooks/useTracks.js
import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import toTrack from "../utils/toTrack";

const shared = {
  tracks: [],
  loadPromise: null,
  status: "idle",
  error: "",
};

const CATALOG_READ_TIMEOUT_MS = 12000;

const boundedCatalogRead = (read) => new Promise((resolve, reject) => {
  const timeout = setTimeout(
    () => reject(new Error("The beat catalog did not respond in time.")),
    CATALOG_READ_TIMEOUT_MS,
  );
  Promise.resolve(read).then(resolve, reject).finally(() => clearTimeout(timeout));
});

const loadTracks = (db) => {
  if (!db || shared.loadPromise) return shared.loadPromise;
  const colRef = collection(db, "tracks");
  shared.status = "loading";
  shared.error = "";
  shared.loadPromise = boundedCatalogRead(getDocs(colRef))
    .then((snap) => {
      shared.tracks = snap.docs.map(toTrack);
      shared.status = "ready";
      return shared.tracks;
    })
    .catch((err) => {
      console.error("[useTracks] catalog read error", err);
      shared.status = "error";
      shared.error = err?.message || "The beat catalog could not be loaded.";
      shared.loadPromise = null;
      return [];
    });
  return shared.loadPromise;
};

export function useTrackCatalog(db) {
  const [tracks, setTracks] = useState(shared.tracks);
  const [status, setStatus] = useState(shared.status);
  const [error, setError] = useState(shared.error);

  useEffect(() => {
    let active = true;

    // Public catalog data changes infrequently. Every mounted consumer shares
    // one server read, while checkout revalidates prices and availability.
    loadTracks(db)?.then((next) => {
      if (active) {
        setTracks(next);
        setStatus(shared.status);
        setError(shared.error);
      }
    });
    setStatus(shared.status);

    return () => {
      active = false;
    };
  }, [db]);

  return {tracks, status, error};
}

export default function useTracks(db) {
  return useTrackCatalog(db).tracks;
}
